// utils/entityTypeValidation.ts

import { CreateEntityTypeRequest } from '@/models/EntityType';
import type {
  GeneralTabFormData,
  Stage,
  Relationship,
} from '../models/EditableList';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateEntityTypeForm = (
  formData: GeneralTabFormData,
  stages: Stage[],
  relationships: Relationship[],
): ValidationResult => {
  // Required fields
  if (!formData.entityTypeName?.trim()) {
    return { isValid: false, error: 'Entity Type Name is required' };
  }

  if (!formData.description?.trim()) {
    return { isValid: false, error: 'Description is required' };
  }

  // Tags validation
  if (formData.tags.length > 0) {
    const tagRegex = /^[a-z0-9-]+$/;

    const invalidTag = formData.tags.find(
      (tag) => tag.length >= 45 || tag.includes(' ') || !tagRegex.test(tag),
    );

    if (invalidTag) {
      return {
        isValid: false,
        error: `Tag "${invalidTag}" is invalid. Tags must be less than 45 characters, contain no spaces, and only include lowercase letters (a-z), numbers (0-9), and hyphen (-)`,
      };
    }
  }

  // Stages validation
  const stagesWithNames = stages.filter((stage) => stage.stage?.trim());

  if (stagesWithNames.length === 0) {
    return {
      isValid: false,
      error:
        'At least 2 stages are required with created and completed statuses',
    };
  }

  const invalidStage = stagesWithNames.find((stage) => !stage.status?.trim());

  if (invalidStage) {
    return {
      isValid: false,
      error: 'All stages must have both stage name and status',
    };
  }

  const hasCreatedStatus = stagesWithNames.filter(
    (stage) => stage.status?.toLowerCase() === 'created',
  ).length;

  if (hasCreatedStatus !== 1) {
    return {
      isValid: false,
      error: 'Stages must contain only one status with "created"',
    };
  }

  const hasCompletedStatus = stagesWithNames.some(
    (stage) => stage.status?.toLowerCase() === 'completed',
  );

  if (!hasCompletedStatus) {
    return {
      isValid: false,
      error: 'Stages must contain at least one status with "completed"',
    };
  }

  // Relationships validation (FIXED FIELD)
  if (relationships.length > 0) {
    const invalidRelationship = relationships.find(
      (rel) => !rel.entityTypeId?.trim(),
    );

    if (invalidRelationship) {
      return {
        isValid: false,
        error: 'All relationships must have a related entity type',
      };
    }
  }

  return { isValid: true };
};

export const normalizeEntityTypeForCompare = (
  data: Partial<CreateEntityTypeRequest> | any,
): CreateEntityTypeRequest => {
  return {
    entityTypeName: data?.entityTypeName ?? '',
    description: data?.description ?? '',
    tags: data?.tags ?? [],
    entityTypeAttributes: data?.entityTypeAttributes ?? {
      type: 'object',
      properties: {},
    },
    statusMapping:
      data?.statusMapping?.map((stage: any) => ({
        stage: stage.stage,
        status: stage.status,
      })) ?? [],
    entityTypeRelationships:
      data?.entityTypeRelationships?.map((rel: any) => ({
        relatedEntityTypeId: rel.relatedEntityTypeId,
      })) ?? [],
  };
};

export function getErrorMessage(
  error: unknown,
  fallback = 'Failed to update entity type. Please try again.',
): string {
  return (
    (error as any)?.response?.data?.message ||
    (error instanceof Error ? error.message : undefined) ||
    (error as any)?.message ||
    fallback
  );
}
